import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DIRECTION, LOCALES, DEFAULT_LOCALE, t as translate } from '@i18n/catalogue.js';
import { formatNumber as fmtNumber, formatDate as fmtDate } from '@i18n/format.js';

const LocaleContext = createContext(null);
const STORAGE_KEY = 'csos.locale';

/**
 * Owns the active locale and keeps the document's `lang`/`dir` in sync, which
 * is what makes the browser lay the whole tree out right-to-left. The logic it
 * delegates to (catalogue, formatting) lives in backend/src/modules/i18n and is
 * unit-tested there.
 */
export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  });

  const dir = DIRECTION[locale] || 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', dir);
    window.localStorage.setItem(STORAGE_KEY, locale);
  }, [locale, dir]);

  const setLocale = useCallback((next) => {
    if (LOCALES.includes(next)) setLocaleState(next);
  }, []);

  const value = useMemo(
    () => ({
      locale,
      dir,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
      formatNumber: (v, opts) => fmtNumber(v, locale, opts),
      formatDate: (v, opts) => fmtDate(v, locale, opts),
    }),
    [locale, dir, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside a LocaleProvider');
  return ctx;
}

export default LocaleContext;
